import { createContext, useContext, useMemo, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import type { StudioEntityRef } from './StudioRail.js';

/**
 * The GM's Studio working context, kept across Studio → table → Studio.
 *
 * StudioGate hard-swaps the table and Studio, so StudioSurface state dies on
 * every exit. This memory lives above the fork, inside the in-session provider
 * tree (App.tsx) that is rebuilt per campaign and torn down on leaving the
 * session / logout — so it lasts exactly one entry into one campaign. The key
 * (member + campaign) is an extra guard: another key reads nothing.
 *
 * Not persisted (no F5), no preview tab / full mode / scroll positions, no
 * form texts (unsaved edits are flushed on unmount; data comes from the live
 * stores). References are ids only: StudioSurface drops the ones whose
 * object is gone once the stores are hydrated.
 */
export interface StudioWorkspaceSnapshot {
  readonly selectedBeatId: string | undefined;
  /** Permanent tabs, in their order. */
  readonly tabs: readonly StudioEntityRef[];
  /** Entity in the main pane; undefined = the selected beat's overview. */
  readonly focusRef: StudioEntityRef | undefined;
  /** Explicitly pinned second pane. */
  readonly pinnedRef: StudioEntityRef | undefined;
}

interface Slot {
  readonly key: string;
  readonly snapshot: StudioWorkspaceSnapshot;
}

interface KeyedId {
  readonly key: string;
  readonly beatId: string;
}

/**
 * Package 4 lives in the same slots: the beat the GM views in the game plan
 * at the table (a local choice, not the current beat) and a one-shot request
 * from the plan to open Studio on one beat's overview.
 */
interface Slots {
  readonly workspace: { current: Slot | null };
  readonly planView: { current: KeyedId | null };
  readonly openBeat: { current: KeyedId | null };
}

const MemoryContext = createContext<Slots | null>(null);

/** Mount point (App.tsx), inside the session tree, above StudioGate. */
export function StudioWorkspaceMemoryProvider({ children }: { children: ReactNode }): JSX.Element {
  const workspace = useRef<Slot | null>(null);
  const planView = useRef<KeyedId | null>(null);
  const openBeat = useRef<KeyedId | null>(null);
  const slots = useMemo((): Slots => ({ workspace, planView, openBeat }), []);
  return <MemoryContext.Provider value={slots}>{children}</MemoryContext.Provider>;
}

export interface StudioWorkspaceMemory {
  /** Snapshot saved under this key, if any. */
  readonly restore: () => StudioWorkspaceSnapshot | undefined;
  readonly save: (snapshot: StudioWorkspaceSnapshot) => void;
  /**
   * The beat whose overview Studio must open (the plan's «Open in Studio»),
   * if one was requested under this key. It wins over the restored beat and
   * main pane; tabs and the pinned pane are restored as usual.
   */
  readonly openBeatRequest: () => string | undefined;
  /** The request is served: a later plain entry restores as before. */
  readonly clearOpenBeatRequest: () => void;
}

/** Without the provider (isolated tests) nothing is kept. */
export function useStudioWorkspaceMemory(key: string): StudioWorkspaceMemory {
  const slots = useContext(MemoryContext);
  return useMemo(
    (): StudioWorkspaceMemory => ({
      restore: (): StudioWorkspaceSnapshot | undefined =>
        slots?.workspace.current?.key === key ? slots.workspace.current.snapshot : undefined,
      save: (snapshot: StudioWorkspaceSnapshot): void => {
        if (slots !== null) slots.workspace.current = { key, snapshot };
      },
      openBeatRequest: (): string | undefined =>
        slots?.openBeat.current?.key === key ? slots.openBeat.current.beatId : undefined,
      clearOpenBeatRequest: (): void => {
        if (slots !== null) slots.openBeat.current = null;
      },
    }),
    [slots, key],
  );
}

export interface PlanViewMemory {
  /** The beat viewed in the plan under this key, if any. */
  readonly viewed: () => string | undefined;
  readonly saveViewed: (beatId: string | undefined) => void;
  /** «Open in Studio»: Studio, once mounted under this key, opens this beat. */
  readonly requestOpenBeat: (beatId: string) => void;
}

/** The game plan's side of the same memory; without the provider nothing is kept. */
export function usePlanViewMemory(key: string): PlanViewMemory {
  const slots = useContext(MemoryContext);
  return useMemo(
    (): PlanViewMemory => ({
      viewed: (): string | undefined =>
        slots?.planView.current?.key === key ? slots.planView.current.beatId : undefined,
      saveViewed: (beatId: string | undefined): void => {
        if (slots === null) return;
        slots.planView.current = beatId === undefined ? null : { key, beatId };
      },
      requestOpenBeat: (beatId: string): void => {
        if (slots !== null) slots.openBeat.current = { key, beatId };
      },
    }),
    [slots, key],
  );
}
